import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  Calendar,
  Users,
  QrCode,
  CheckCircle,
  Plus,
  ArrowRight
} from 'lucide-react';

const AdminDashboard = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalEvents: 0,
    activeEvents: 0,
    totalAttendees: 0,
    totalScans: 0
  });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await api.get('/events');
      const eventsList = response.data || [];
      setEvents(eventsList.slice(0, 5)); // Show recent 5
      
      // Calculate stats
      const totalAttendees = eventsList.reduce((sum, e) => sum + (e.actual_attendees || 0), 0);
      setStats({
        totalEvents: eventsList.length,
        activeEvents: eventsList.length, // All events are active
        totalAttendees,
        totalScans: 0 // Would need separate API call
      });
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { label: 'Total Events', value: stats.totalEvents, icon: Calendar, color: 'bg-blue-500' },
    { label: 'Active Events', value: stats.activeEvents, icon: CheckCircle, color: 'bg-green-500' },
    { label: 'Total Attendees', value: stats.totalAttendees, icon: Users, color: 'bg-purple-500' },
    { label: 'Total Scans', value: stats.totalScans, icon: QrCode, color: 'bg-orange-500' },
  ];

  return (
    <AdminLayout title="Dashboard">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <Card key={stat.label} data-testid={`stat-${stat.label.toLowerCase().replace(' ', '-')}`}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">{stat.label}</p>
                    <p className="text-3xl font-bold">{stat.value}</p>
                  </div>
                  <div className={`p-3 rounded-full ${stat.color}`}>
                    <Icon className="h-6 w-6 text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Recent Events */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Recent Events</CardTitle>
          <Link to="/admin/events">
            <Button variant="outline" size="sm">
              <Plus className="h-4 w-4 mr-2" /> New Event
            </Button>
          </Link>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : events.length === 0 ? (
            <div className="text-center py-8">
              <Calendar className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 mb-4">No events yet</p>
              <Link to="/admin/events">
                <Button>
                  <Plus className="h-4 w-4 mr-2" /> Create Your First Event
                </Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {events.map((event) => (
                <Link
                  key={event.event_id}
                  to={`/admin/events/${event.event_id}`}
                  className="flex items-center justify-between p-4 rounded-lg border hover:bg-gray-50 transition-colors"
                  data-testid={`event-card-${event.event_id}`}
                >
                  <div className="flex items-center gap-4">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: event.branding_color || '#3B82F6' }}
                    />
                    <div>
                      <h3 className="font-medium">{event.event_name}</h3>
                      <p className="text-sm text-gray-500">
                        {event.event_location} • {event.actual_attendees || 0} attendees
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <ArrowRight className="h-5 w-5 text-gray-400" />
                  </div>
                </Link>
              ))}
              
              {events.length > 0 && (
                <Link to="/admin/events" className="block text-center text-primary hover:underline pt-4">
                  View all events →
                </Link>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </AdminLayout>
  );
};

export default AdminDashboard;
